import React from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import siteUrl from "../../../../const/const";
import { cn } from "@/lib/utils";

interface QuizValue {
  text: string;
  answer: string;
  comment: string | null;
  rekvizit: string | null;
  source_media: string | null;
}

interface QuizTheme {
  id: number;
  name: string;
  information?: string;
  values: QuizValue[];
  sources: string[];
  authors: { fullname: string }[];
}

interface QuizProps {
  theme: QuizTheme;
  opens: string[];
  toggleOpen: (themeId: number, questionIndex: number) => void;
}

const TextLines: React.FC<{ text: string; indent?: boolean }> = ({ text, indent }) =>
  <>
    {text.split("/-/").map((m, i) => (
      <span key={i}>
        {indent && i ? <span>&nbsp;&nbsp;</span> : null}
        {m}
        <br />
      </span>
    ))}
  </>;

const Quiz: React.FC<QuizProps> = ({ theme: t, opens, toggleOpen }) => {
  const { t: tr } = useTranslation();

  return (
    <div className="border-b border-gray-100 dark:border-gray-800 py-4 last:border-0">
      {/* Theme name */}
      <h4 className="text-sm font-bold text-gray-900 dark:text-gray-50 mb-1">
        <span className="text-gray-500 dark:text-gray-400 font-semibold">Mövzu: </span>
        {t.name}
      </h4>
      {t.information && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">{t.information}</p>
      )}

      {/* Questions */}
      <div className="space-y-3">
        {t.values.map((va, index) => {
          const isOpen = opens.includes(`${t.id}-${index}`);
          return (
            <div key={index}>
              {/* Photo rekvizit */}
              {va.rekvizit !== null && (
                <>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                    {index + 1}. <span className="font-bold">Foto:</span>
                  </p>
                  <img
                    className="max-h-56 max-w-xs rounded-lg mb-1"
                    alt={va.rekvizit}
                    src={`${siteUrl}images/${va.rekvizit}`}
                  />
                </>
              )}

              {/* Question text */}
              <p className="text-gray-800 dark:text-gray-100 font-semibold text-sm break-words">
                {va.rekvizit !== null ? (
                  <TextLines text={va.text} />
                ) : (
                  <>{index + 1}. <TextLines text={va.text} indent /></>
                )}
              </p>

              {/* Reveal toggle */}
              <button
                onClick={() => toggleOpen(t.id, index)}
                className="flex items-center gap-1 text-blue-500 hover:text-blue-700 transition-colors mt-1 dark:text-indigo-300 dark:hover:text-indigo-200"
                aria-label="Cavabı göstər / gizlət"
              >
                <ChevronDown
                  className={cn(
                    "w-4 h-4 transition-transform duration-300",
                    isOpen && "rotate-180"
                  )}
                />
              </button>

              {/* Collapsible answer block */}
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isOpen ? "max-h-[2000px] opacity-100 mt-1" : "max-h-0 opacity-0"
                )}
              >
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700 dark:text-gray-200 font-medium">
                    <span className="font-bold text-gray-900 dark:text-gray-50">Cavab: </span>
                    {va.answer}
                  </p>
                  {va.comment?.trim() && (
                    <p className="text-gray-700 dark:text-gray-200">
                      <span className="font-bold text-gray-900 dark:text-gray-50">Şərh: </span>
                      {va.comment}
                    </p>
                  )}
                  {va.source_media !== null && (
                    <div>
                      <p className="font-bold text-gray-900 dark:text-gray-50 mb-1">Foto mənbə:</p>
                      <img
                        className="max-h-56 max-w-xs rounded-lg"
                        alt={va.source_media}
                        src={`${siteUrl}images/${va.source_media}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Theme-level authors (always visible) */}
      {t.authors?.length > 0 && (
        <p className="text-sm text-gray-700 dark:text-gray-200 mt-3">
          <span className="font-bold text-gray-900 dark:text-gray-50">
            {t.authors.length > 1 ? tr("packages.authorPlural") : tr("packages.authorSingular")}
          </span>{" "}
          {t.authors.map((a, i) => (
            <React.Fragment key={i}>
              {i > 0 ? ", " : ""}
              {a.fullname}
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

export default Quiz;
